/**
 * Mutations for the [measurements store]{link module:store/measurements}
 *
 * @module store/measurements/mutations
 */

import { SENSOR_TYPE_FLAG_VALUES, THERMAL_FOR_DASHBOARD, TIMEOUTS_IN_MS } from '@/store/modules/measurements'

export function SET_DEVICE_RELATIONS(state, { deviceRelations }) {
  state.deviceRelations = deviceRelations
}

export function SET_SENSORS_DIRECT(state, sensors) {
  state.sensors = sensors
}

export function SET_SENSOR_VALS_DIRECT(state, vals) {
  state.vals = vals
}

export function ADD_SENSOR_SERIES(state, { sensorId, ts, unit, values }) {
  if (!state.vals[sensorId]) state.vals[sensorId] = { values: [], ts: [] }

  state.vals[sensorId].ts.push(...ts.map((t) => moment(t)))
  state.vals[sensorId].unit = unit
  state.vals[sensorId].values.push(...values)

  sortSensorSeries(state, sensorId)
}

const sortSensorSeries = (state, sensorId) => {
  const series = []
  for (let i = 0; i < state.vals[sensorId].ts.length; i++) {
    series.push({
      t: state.vals[sensorId].ts[i],
      value: state.vals[sensorId].values[i]
    })
  }

  series.sort((a, b) => a.t.valueOf() - b.t.valueOf())

  state.vals[sensorId].ts = series.map((x) => x.t)
  state.vals[sensorId].values = series.map((x) => x.value)
}

export function SET_SENSORS(state, { sensors, prepareVals = true, filterSensorsForDashboard = false }) {
  for (const apiKey in sensors) {
    const newSensor = sensors[apiKey]

    if (filterSensorsForDashboard) {
      if (
        newSensor.sensorTypeFlag & SENSOR_TYPE_FLAG_VALUES.thermal &&
        !THERMAL_FOR_DASHBOARD.includes(newSensor.sensorTypeId)
      ) {
        continue
      }
    }

    state.sensors[apiKey] = newSensor

    if (prepareVals && !state.vals[apiKey]) {
      state.vals[apiKey] = 0
    }
  }
}

export function FIX_SENSOR_REFS(state, refSensors) {
  for (const refSensor of refSensors) {
    if (!refSensor.refId) continue

    const sensor = Object.values(state.sensors).filter((s) => s.id === refSensor.refId)[0]

    if (!sensor) continue

    state.sensors[refSensor.apiKey].refLabel = sensor.label
    state.sensors[refSensor.apiKey].refDeviceLabel = sensor.deviceLabel
    state.sensors[sensor.apiKey].refId = refSensor.id
    state.sensors[sensor.apiKey].refLabel = refSensor.label
    state.sensors[sensor.apiKey].refDeviceLabel = refSensor.deviceLabel
  }
}

export function SET_SENSORS_HAS_PERMISSION_RESTRICTION(state, hasPermissionRestriction) {
  state.permission.sensors.hasPermissionRestriction = hasPermissionRestriction
}

export function SET_SENSOR_VALS(state, vals) {
  Object.assign(state.vals, vals)
}

export function SET_SENSOR_SEQS(state, seqs) {
  state.seqs = seqs
}

export function SET_DEVICES(state, devices) {
  for (const id in devices) {
    state.devices[id] = devices[id]
  }
}

export function UPDATE_DEVICE(state, data) {
  if (data) {
    if (state.devices[data.id]) {
      for (const key in data.attributes) {
        state.devices[data.id][key] = data.attributes[key]
      }
    }
  }
}

export function SAVE_STREAM(state, { data, addFiller = false }) {
  if (data.length && data[0].t_unix) {
    data.sort((a, b) => {
      const nameA = a.t_unix
      const nameB = b.t_unix

      if (nameA < nameB) {
        return -1
      }
      return 1
    })
  }

  for (const row of data) {
    if (Object.keys(row).length < 2) continue

    for (const key in row) {
      const pkey = key.replace('m_', '')
      if (!state.vals[pkey]) {
        state.vals[pkey] = []
      }
      state.vals[pkey].push(row[key])
    }
  }

  if (addFiller) {
    for (const key in state.vals) {
      state.vals[key].push('...')
    }
  }
}

/**
 * Mutation to change the current instant update values
 *
 * Checks if the new Values are more recent. Afterwards inserts them into the state
 *
 * @param {Object} vals of form { <apiKey>: [{string} datetime in ISO8691, {float|null} value] }
 */
export function SET_INSTANT_UPDATE_VALS(state, vals) {
  const tClearThershold = moment().subtract(TIMEOUTS_IN_MS.clearValsFrequency, 'milliseconds')

  for (const apiKey in vals) {
    if (!vals[apiKey]) continue

    const val = {
      t: moment(vals[apiKey][0]),
      value: vals[apiKey][1]
    }

    if (state.vals[apiKey] && val.t < state.vals[apiKey].t) continue
    if (val.t < tClearThershold) {
      val.value = null
    }

    state.vals[apiKey] = val
  }
}

export function SET_INSTANT_UPDATE_STATUS_VAL(state, val) {
  const apiKey = Object.keys(val).find((v) => v !== 'datetime')
  if (!apiKey) return

  const v = {
    t: moment(val.datetime),
    value: val[apiKey]
  }

  if (state.vals[apiKey] && v.t < state.vals[apiKey].t) return

  state.vals[apiKey] = v
}

export function CLEAR_OLD_VALS(state) {
  const tClearThershold = moment().subtract(TIMEOUTS_IN_MS.clearValsFrequency, 'milliseconds')
  for (const key in state.vals) {
    if (state.sensors[key].type === 'sensors' && state.vals[key].t < tClearThershold) {
      state.vals[key].value = null
    }
  }
}

export function RESET_STORE(state) {
  state.devices = {}
  state.sensors = {}
  state.vals = {}
}
